import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';

/**
 * Finds the start of the current line within a text block.
 * Returns the document position where the current line begins.
 */
function findLineStart(doc: any, pos: number, blockStart: number): number {
  const textBefore = doc.textBetween(blockStart, pos, '\0', '\0');
  const lastNewline = textBefore.lastIndexOf('\n');
  return lastNewline === -1 ? blockStart : blockStart + lastNewline + 1;
}

/**
 * Gets all line start positions within a selection range.
 */
function getLineStartsInRange(doc: any, from: number, to: number, blockStart: number): number[] {
  const lineStarts: number[] = [];
  const text = doc.textBetween(blockStart, to, '\0', '\0');

  // First line start
  const firstLineStart = findLineStart(doc, from, blockStart);
  lineStarts.push(firstLineStart);

  // Find all newlines between from and to
  let searchPos = firstLineStart - blockStart;
  while (searchPos < text.length) {
    const newlinePos = text.indexOf('\n', searchPos);
    if (newlinePos === -1 || blockStart + newlinePos >= to) break;
    const lineStart = blockStart + newlinePos + 1;
    if (lineStart < to) {
      lineStarts.push(lineStart);
    }
    searchPos = newlinePos + 1;
  }

  return lineStarts;
}

export const TabHandler = Extension.create({
  name: 'tabHandler',

  // High priority to ensure Tab is captured before other extensions
  priority: 1000,

  addKeyboardShortcuts() {
    return {
      Tab: () => {
        const { editor } = this;

        // If in a list item, sink it (indent)
        if (editor.can().sinkListItem('listItem')) {
          return editor.commands.sinkListItem('listItem');
        }
        if (editor.can().sinkListItem('taskItem')) {
          return editor.commands.sinkListItem('taskItem');
        }

        // Insert a tab character using a transaction (works in code blocks too)
        const { state, view } = editor;
        const { from, to } = state.selection;
        const tr = state.tr.insertText('\t', from, to);
        view.dispatch(tr);
        return true;
      },

      'Shift-Tab': () => {
        const { editor } = this;

        // If in a list item, lift it (unindent)
        if (editor.can().liftListItem('listItem')) {
          return editor.commands.liftListItem('listItem');
        }
        if (editor.can().liftListItem('taskItem')) {
          return editor.commands.liftListItem('taskItem');
        }

        // For code blocks and regular text, remove leading whitespace from current line
        const { state, view } = editor;
        const { $from } = state.selection;
        const blockStart = $from.start();
        const lineStart = findLineStart(state.doc, $from.pos, blockStart);

        // Get text from line start to find leading whitespace
        const blockEnd = $from.end();
        const lineText = state.doc.textBetween(lineStart, blockEnd, '\0', '\0');
        const firstNewline = lineText.indexOf('\n');
        const currentLineText = firstNewline === -1 ? lineText : lineText.substring(0, firstNewline);

        // Determine how much whitespace to remove
        let charsToRemove = 0;
        if (currentLineText.startsWith('\t')) {
          charsToRemove = 1;
        } else if (currentLineText.startsWith('    ')) {
          charsToRemove = 4;
        } else if (currentLineText.startsWith('  ')) {
          charsToRemove = 2;
        } else if (currentLineText.startsWith(' ')) {
          charsToRemove = 1;
        }

        if (charsToRemove > 0) {
          const tr = state.tr.delete(lineStart, lineStart + charsToRemove);
          view.dispatch(tr);
        }

        return true;
      },
    };
  },

  addProseMirrorPlugins() {
    const editor = this.editor;

    return [
      new Plugin({
        key: new PluginKey('tabHandlerPlugin'),
        props: {
          handleKeyDown(view, event) {
            // Handle Tab key
            if (event.key === 'Tab' && !event.altKey && !event.ctrlKey && !event.metaKey) {
              event.preventDefault();

              const { state } = view;
              const { from, to } = state.selection;
              const { $from } = state.selection;
              const blockStart = $from.start();
              const blockEnd = $from.end();

              // Check if multiple lines are selected
              const lineStarts = getLineStartsInRange(state.doc, from, to, blockStart);
              const isMultiLine = lineStarts.length > 1;

              if (event.shiftKey) {
                // Shift-Tab: unindent
                // Check for list items first
                if (editor.can().liftListItem('listItem')) {
                  editor.commands.liftListItem('listItem');
                  return true;
                }
                if (editor.can().liftListItem('taskItem')) {
                  editor.commands.liftListItem('taskItem');
                  return true;
                }

                if (isMultiLine) {
                  // Unindent all selected lines
                  let tr = state.tr;
                  let offset = 0;

                  for (const lineStart of lineStarts) {
                    const adjustedLineStart = lineStart + offset;
                    const lineText = tr.doc.textBetween(adjustedLineStart, tr.doc.resolve(adjustedLineStart).end(), '\0', '\0');
                    const firstNewline = lineText.indexOf('\n');
                    const currentLineText = firstNewline === -1 ? lineText : lineText.substring(0, firstNewline);

                    let charsToRemove = 0;
                    if (currentLineText.startsWith('\t')) {
                      charsToRemove = 1;
                    } else if (currentLineText.startsWith('    ')) {
                      charsToRemove = 4;
                    } else if (currentLineText.startsWith('  ')) {
                      charsToRemove = 2;
                    } else if (currentLineText.startsWith(' ')) {
                      charsToRemove = 1;
                    }

                    if (charsToRemove > 0) {
                      tr = tr.delete(adjustedLineStart, adjustedLineStart + charsToRemove);
                      offset -= charsToRemove;
                    }
                  }

                  view.dispatch(tr);
                } else {
                  // Single line unindent
                  const lineStart = findLineStart(state.doc, from, blockStart);
                  const lineText = state.doc.textBetween(lineStart, blockEnd, '\0', '\0');
                  const firstNewline = lineText.indexOf('\n');
                  const currentLineText = firstNewline === -1 ? lineText : lineText.substring(0, firstNewline);

                  let charsToRemove = 0;
                  if (currentLineText.startsWith('\t')) {
                    charsToRemove = 1;
                  } else if (currentLineText.startsWith('    ')) {
                    charsToRemove = 4;
                  } else if (currentLineText.startsWith('  ')) {
                    charsToRemove = 2;
                  } else if (currentLineText.startsWith(' ')) {
                    charsToRemove = 1;
                  }

                  if (charsToRemove > 0) {
                    const tr = state.tr.delete(lineStart, lineStart + charsToRemove);
                    view.dispatch(tr);
                  }
                }
              } else {
                // Tab: indent
                // Check for list items first
                if (editor.can().sinkListItem('listItem')) {
                  editor.commands.sinkListItem('listItem');
                  return true;
                }
                if (editor.can().sinkListItem('taskItem')) {
                  editor.commands.sinkListItem('taskItem');
                  return true;
                }

                if (isMultiLine) {
                  // Indent all selected lines
                  let tr = state.tr;
                  let offset = 0;

                  for (const lineStart of lineStarts) {
                    const adjustedLineStart = lineStart + offset;
                    tr = tr.insertText('\t', adjustedLineStart, adjustedLineStart);
                    offset += 1;
                  }

                  view.dispatch(tr);
                } else {
                  // Single cursor/selection: insert tab character
                  const tr = state.tr.insertText('\t', from, to);
                  view.dispatch(tr);
                }
              }

              return true;
            }

            return false;
          },
        },
      }),
    ];
  },
});
